"use client";

import {
  FILTER_DROPDOWN_MAX_WIDTH,
  FILTER_DROPDOWN_MIN_WIDTH,
  SearchableSelect,
  type SearchableSelectOption,
} from "@/components/ui/primitives/searchable-select";
import { cn } from "@/lib/utils/general-utils";
import { X as XMarkIcon } from "lucide-react";
import { useMemo, type ReactNode } from "react";

export type OutcomeToolbarFilterAction =
  | { type: "toggle-pass" }
  | { type: "toggle-fail" }
  | { type: "toggle-unassigned" }
  | { type: "set-ids"; ids: readonly string[] }
  | { type: "remove"; token: string }
  | { type: "clear" };

export type OutcomeToolbarFilterPill = {
  value: string;
  label: string;
};

export interface OutcomeToolbarFilterSelectProps {
  readonly value: string[];
  readonly onChange: (value: string[]) => void;
  readonly options: SearchableSelectOption[];
  readonly placeholder?: string;
  readonly searchPlaceholder?: string;
  readonly disabled?: boolean;
  readonly dropdownHeader?: ReactNode;
  readonly "aria-label"?: string;
  readonly selectedIds: readonly string[];
  readonly unassignedMode: boolean;
  readonly pass: boolean;
  readonly fail: boolean;
  readonly unassignedNotice: string;
  readonly pills: readonly OutcomeToolbarFilterPill[];
  readonly applyAction: (action: OutcomeToolbarFilterAction) => string[];
}

function OutcomeChip({
  label,
  active,
  dotClass,
  onClick,
}: {
  label: string;
  active: boolean;
  dotClass: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors whitespace-nowrap",
        active
          ? "border-gray-800 bg-gray-800 text-white"
          : "border-gray-200 bg-white text-gray-700 hover:border-gray-300",
      )}
    >
      <span
        className={cn(
          "h-1.5 w-1.5 rounded-full",
          active ? "bg-white" : dotClass,
        )}
      />
      {label}
    </button>
  );
}

export function OutcomeToolbarFilterSelect({
  value,
  onChange,
  options,
  placeholder = "Filter",
  searchPlaceholder,
  disabled = false,
  dropdownHeader,
  "aria-label": ariaLabel,
  selectedIds,
  unassignedMode,
  pass,
  fail,
  unassignedNotice,
  pills,
  applyAction,
}: OutcomeToolbarFilterSelectProps) {
  const triggerAria = pills.length
    ? `${placeholder}: ${pills.map((pill) => pill.label).join(", ")}`
    : (ariaLabel ?? placeholder);

  return (
    <SearchableSelect
      multiple
      value={[...selectedIds]}
      onChange={(ids) =>
        onChange(
          applyAction({
            type: "set-ids",
            ids,
          }),
        )
      }
      options={options}
      placeholder={placeholder}
      searchPlaceholder={searchPlaceholder}
      searchable
      disabled={disabled}
      minDropdownWidth={FILTER_DROPDOWN_MIN_WIDTH}
      maxDropdownWidth={FILTER_DROPDOWN_MAX_WIDTH}
      aria-label={triggerAria}
      showFieldPrefix
      hasSelection={value.length > 0}
      pillLabels={pills.map((pill) => pill.label)}
      listMuted={unassignedMode}
      onClear={() => onChange(applyAction({ type: "clear" }))}
      renderPills={({ disabled: triggerDisabled }) =>
        pills.map((pill) => (
          <span
            key={pill.value}
            className="inline-flex shrink-0 items-center gap-1 rounded-md bg-muted px-2 py-0.5 text-xs text-foreground"
          >
            <span className="max-w-[12rem] truncate">{pill.label}</span>
            {!triggerDisabled && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onChange(
                    applyAction({
                      type: "remove",
                      token: pill.value,
                    }),
                  );
                }}
                className="inline-flex items-center justify-center rounded-sm text-muted-foreground transition-colors hover:text-foreground"
                aria-label={`Remove ${pill.label}`}
              >
                <XMarkIcon className="h-3 w-3" />
              </button>
            )}
          </span>
        ))
      }
      dropdownHeader={
        <div className="flex flex-col gap-2">
          <div className="flex flex-nowrap items-center gap-1.5">
            <OutcomeChip
              label="Pass"
              active={pass}
              dotClass="bg-emerald-500"
              onClick={() => onChange(applyAction({ type: "toggle-pass" }))}
            />
            <OutcomeChip
              label="Fail"
              active={fail}
              dotClass="bg-red-500"
              onClick={() => onChange(applyAction({ type: "toggle-fail" }))}
            />
            <OutcomeChip
              label="Not assigned"
              active={unassignedMode}
              dotClass="bg-gray-400"
              onClick={() =>
                onChange(applyAction({ type: "toggle-unassigned" }))
              }
            />
          </div>
          {unassignedMode ? (
            <p className="text-[11px] leading-snug text-text-tertiary">
              {unassignedNotice}
            </p>
          ) : null}
          {dropdownHeader}
        </div>
      }
    />
  );
}

export function useOutcomeToolbarPills(
  value: readonly string[],
  options: readonly SearchableSelectOption[],
  triggerPills: (
    values: readonly string[],
    labelById: ReadonlyMap<string, string>,
  ) => OutcomeToolbarFilterPill[],
): OutcomeToolbarFilterPill[] {
  const labelById = useMemo(() => {
    const map = new Map<string, string>();
    for (const option of options) {
      map.set(option.value, option.label);
    }
    return map;
  }, [options]);

  return useMemo(
    () => triggerPills(value, labelById),
    [value, labelById, triggerPills],
  );
}
